import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { OK, CREATED } from 'http-status-codes';

import { safeResolveAsync } from "../../utils";
import { actions, types } from './duck';
import * as api from './api';

// ---------------------------------------------------------------------------
// COUPON DAILY STATS
// ---------------------------------------------------------------------------
export function* getDailyStats({ params }) {
  const couponId = params && params.coupon_id ? String(params.coupon_id) : 'all';
  try {
    yield put(actions.getDailyStatsRequest(couponId));
    const response = yield call(api.getDailyStats, params);
    if (response.status === OK) {
      yield put(actions.getDailyStatsSuccess(couponId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load daily stats';
      yield put(actions.getDailyStatsFailure(couponId, detail));
    }
  } catch (error) {
    yield put(actions.getDailyStatsFailure(couponId, error.message));
  }
}

// ---------------------------------------------------------------------------
// POSITION BUCKETS
// ---------------------------------------------------------------------------
export function* getPositionBuckets({ params }) {
  const couponId = params && params.coupon_id ? String(params.coupon_id) : 'all';
  try {
    yield put(actions.getPositionBucketsRequest(couponId));
    const response = yield call(api.getPositionBuckets, params);
    if (response.status === OK) {
      yield put(actions.getPositionBucketsSuccess(couponId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load position buckets';
      yield put(actions.getPositionBucketsFailure(couponId, detail));
    }
  } catch (error) {
    yield put(actions.getPositionBucketsFailure(couponId, error.message));
  }
}

// ---------------------------------------------------------------------------
// SCREEN STATS
// ---------------------------------------------------------------------------
export function* getScreenStats({ params }) {
  const couponId = params && params.coupon_id ? String(params.coupon_id) : 'all';
  try {
    yield put(actions.getScreenStatsRequest(couponId));
    const response = yield call(api.getScreenStats, params);
    if (response.status === OK) {
      yield put(actions.getScreenStatsSuccess(couponId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load screen stats';
      yield put(actions.getScreenStatsFailure(couponId, detail));
    }
  } catch (error) {
    yield put(actions.getScreenStatsFailure(couponId, error.message));
  }
}

// ---------------------------------------------------------------------------
// RANKING
// ---------------------------------------------------------------------------
export function* getRanking({ params }) {
  try {
    yield put(actions.getRankingRequest());
    const response = yield call(api.getRanking, params);
    if (response.status === OK) {
      yield put(actions.getRankingSuccess(
        response.data.results || response.data,
        (params && params.days) || null,
      ));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load ranking';
      yield put(actions.getRankingFailure(detail));
    }
  } catch (error) {
    yield put(actions.getRankingFailure(error.message));
  }
}

// ---------------------------------------------------------------------------
// BUSINESS STATS
// ---------------------------------------------------------------------------
export function* getBusinessStats({ params }) {
  const businessId = params && params.business_id ? String(params.business_id) : 'all';
  // `slot` lets two independent panels (e.g. two RedemptionsChart instances)
  // fetch the same business's stats concurrently without one clobbering the
  // other's reducer slice — see duck.js. It's a client-side routing concern
  // only, so it's stripped out of what actually goes to the API below.
  const slot = (params && params.slot) || 'default';
  const { slot: _slot, ...apiParams } = params || {};
  try {
    yield put(actions.getBusinessStatsRequest(businessId, slot));
    const response = yield call(api.getBusinessStats, apiParams);
    if (response.status === OK) {
      yield put(actions.getBusinessStatsSuccess(businessId, slot, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load business stats';
      yield put(actions.getBusinessStatsFailure(businessId, slot, detail));
    }
  } catch (error) {
    yield put(actions.getBusinessStatsFailure(businessId, slot, error.message));
  }
}

// ---------------------------------------------------------------------------
// USER COUPON DAILY STATS
// ---------------------------------------------------------------------------
export function* getUserCouponStats({ params }) {
  const userId = params && params.user_id ? String(params.user_id) : 'all';
  try {
    yield put(actions.getUserCouponStatsRequest(userId));
    const response = yield call(api.getUserCouponStats, params);
    if (response.status === OK) {
      yield put(actions.getUserCouponStatsSuccess(userId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load user coupon stats';
      yield put(actions.getUserCouponStatsFailure(userId, detail));
    }
  } catch (error) {
    yield put(actions.getUserCouponStatsFailure(userId, error.message));
  }
}

// ---------------------------------------------------------------------------
// USER DAILY STATS
// ---------------------------------------------------------------------------
export function* getUserDailyStats({ params }) {
  const userId = params && params.user_id ? String(params.user_id) : 'all';
  try {
    yield put(actions.getUserDailyStatsRequest(userId));
    const response = yield call(api.getUserDailyStats, params);
    if (response.status === OK) {
      yield put(actions.getUserDailyStatsSuccess(userId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load user daily stats';
      yield put(actions.getUserDailyStatsFailure(userId, detail));
    }
  } catch (error) {
    yield put(actions.getUserDailyStatsFailure(userId, error.message));
  }
}

// ---------------------------------------------------------------------------
// GUEST DAILY STATS (same table as USER DAILY STATS, guest-scoped)
// ---------------------------------------------------------------------------
export function* getGuestDailyStats({ params }) {
  // Always 'all': guest fetches aren't scoped to a single session id the
  // way an admin might scope getUserDailyStats to one user_id.
  const userId = 'all';
  try {
    yield put(actions.getGuestDailyStatsRequest(userId));
    const response = yield call(api.getGuestDailyStats, params);
    if (response.status === OK) {
      yield put(actions.getGuestDailyStatsSuccess(userId, response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load guest daily stats';
      yield put(actions.getGuestDailyStatsFailure(userId, detail));
    }
  } catch (error) {
    yield put(actions.getGuestDailyStatsFailure(userId, error.message));
  }
}

// ---------------------------------------------------------------------------
// USER RAW EVENTS (granular, 14-day window)
// ---------------------------------------------------------------------------
export function* getUserRawEvents({ params }) {
  try {
    yield put(actions.getUserRawEventsRequest());
    const response = yield call(api.getUserRawEvents, params);
    if (response.status === OK) {
      yield put(actions.getUserRawEventsSuccess(response.data.results || response.data));
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to load raw events';
      yield put(actions.getUserRawEventsFailure(detail));
    }
  } catch (error) {
    yield put(actions.getUserRawEventsFailure(error.message));
  }
}

// ---------------------------------------------------------------------------
// EVENT INGEST (batch) — takeEvery so concurrent flushes don't cancel
// ---------------------------------------------------------------------------
export function* sendEvents({ events, resolve }) {
  try {
    yield put(actions.sendEventsRequest());
    const response = yield call(api.sendEvents, events);
    if (response.status === CREATED || response.status === OK) {
      yield put(actions.sendEventsSuccess(response.data.accepted || events.length));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      const detail = (response.data && response.data.detail) || 'Failed to send events';
      yield put(actions.sendEventsFailure(detail));
      yield safeResolveAsync(resolve, { success: false });
    }
  } catch (error) {
    yield put(actions.sendEventsFailure(error.message));
    yield safeResolveAsync(resolve, { success: false });
  }
}

// ---------------------------------------------------------------------------
// ROOT WATCHER
// ---------------------------------------------------------------------------
export default function* saga() {
  yield takeLatest(types.GET_DAILY_STATS,       getDailyStats);
  yield takeLatest(types.GET_POSITION_BUCKETS,  getPositionBuckets);
  yield takeLatest(types.GET_SCREEN_STATS,      getScreenStats);
  yield takeLatest(types.GET_RANKING,           getRanking);
  // takeEvery, not takeLatest: two RedemptionsChart instances can each have
  // a getBusinessStats request in flight at once (different slots), and
  // takeLatest would cancel the first whenever the second started — which
  // was the root cause of chart 1 never receiving data on initial mount.
  // Trade-off: if the SAME slot's filter is changed again before its first
  // request resolves, both responses now land instead of the newer one
  // winning — acceptable here since duck.js's per-slot keying means the
  // last one to actually arrive still overwrites, so the stale case is only
  // a possible flash, not stuck-wrong data.
  yield takeEvery(types.GET_BUSINESS_STATS,    getBusinessStats);
  yield takeLatest(types.GET_USER_DAILY_STATS,   getUserDailyStats);
  yield takeLatest(types.GET_GUEST_DAILY_STATS,  getGuestDailyStats);
  yield takeLatest(types.GET_USER_COUPON_STATS,  getUserCouponStats);
  yield takeLatest(types.GET_USER_RAW_EVENTS,    getUserRawEvents);
  // takeEvery: event batches must not cancel each other
  yield takeEvery(types.SEND_EVENTS,            sendEvents);
}